import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, ConfigProvider, DatePicker, Empty, List, Space, Spin, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, EnvironmentOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AirportSelector from "../../components/common/AirportSelector";
import { listChecklistStations, listStationFlights } from "../../api/rampChecklistApi";
import { isFallbackTz, resolveTz, utcToLocalHm } from "./rampChecklistTime";

const { Title, Text } = Typography;

const STATION_STORAGE_KEY = "rampChecklist.lastStation";

const STATUS_COLORS = {
    SCHEDULED: "default",
    DELAYED: "warning",
    DEPARTED: "processing",
    COMPLETED: "success",
    DIVERTED: "warning",
    CANCELLED: "error",
    PLAN_CANCELLED: "error",
    REPLACED: "default",
};

const progressTag = (checklist) => {
    if (!checklist) return null;
    if (checklist.completed) {
        return <Tag color="success">已完成</Tag>;
    }
    if (checklist.initialized) {
        return <Tag color="processing">进行中 {checklist.checked_count}/{checklist.total_count}</Tag>;
    }
    return <Tag>未开始</Tag>;
};

export default function FlightListPage() {
    const navigate = useNavigate();
    const [station, setStation] = useState(() => localStorage.getItem(STATION_STORAGE_KEY) || undefined);
    const [localDate, setLocalDate] = useState(() => dayjs());
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [stations, setStations] = useState([]);
    const [stationsLoading, setStationsLoading] = useState(false);

    const tzId = useMemo(() => resolveTz(data?.tz), [data?.tz]);
    const tzFallback = isFallbackTz(data?.tz);

    const fetchFlights = useCallback(async () => {
        if (!station) {
            setData(null);
            return;
        }
        setLoading(true);
        try {
            const res = await listStationFlights(station, localDate.format("YYYY-MM-DD"));
            setData(res.data);
        } catch (err) {
            message.error("查询本站航班失败，请稍后重试");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [station, localDate]);

    useEffect(() => {
        fetchFlights();
    }, [fetchFlights]);

    // 站点下拉只列"有出发航班"的机场，避免堆满从没飞过的机场。
    useEffect(() => {
        let mounted = true;
        setStationsLoading(true);
        listChecklistStations()
            .then((res) => {
                if (mounted) setStations(res.data?.items || []);
            })
            .catch(() => {
                if (mounted) message.error("加载站点列表失败");
            })
            .finally(() => {
                if (mounted) setStationsLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const handleStationChange = (value) => {
        setStation(value);
        if (value) {
            localStorage.setItem(STATION_STORAGE_KEY, value);
        }
    };

    const items = data?.items || [];

    return (
        <ConfigProvider componentSize="large">
            <div style={{ maxWidth: 720, margin: "0 auto", padding: 12 }}>
                <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate("/")} style={{ paddingLeft: 0, marginBottom: 4 }}>
                    返回系统
                </Button>
                <Title level={4} style={{ marginTop: 0 }}>
                    过站检查单 · 本站航班
                </Title>

                <Card size="small" style={{ marginBottom: 12 }}>
                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <AirportSelector
                                valueType="iata_code"
                                airports={stations}
                                loading={stationsLoading}
                                value={station}
                                onChange={handleStationChange}
                                placeholder="选择本站机场"
                                style={{ flex: "1 1 200px", minWidth: 180 }}
                                allowClear
                            />
                            <DatePicker
                                value={localDate}
                                onChange={(value) => setLocalDate(value || dayjs())}
                                allowClear={false}
                                format="YYYY-MM-DD"
                                style={{ flex: "0 0 auto" }}
                            />
                            <Button icon={<ReloadOutlined />} onClick={fetchFlights} disabled={!station} style={{ flex: "0 0 auto" }}>
                                刷新
                            </Button>
                        </div>
                        {data && (
                            <Space size={4}>
                                <EnvironmentOutlined />
                                <Text type="secondary">当地时区 {tzId}</Text>
                                {tzFallback && <Tag color="warning">本机时区</Tag>}
                            </Space>
                        )}
                    </Space>
                </Card>

                <Spin spinning={loading}>
                    {!station ? (
                        <Empty description="请先选择本站机场" />
                    ) : items.length === 0 ? (
                        <Empty description={loading ? "查询中…" : "当天没有从本站出发的航班"} />
                    ) : (
                        <List
                            dataSource={items}
                            rowKey="leg_id"
                            renderItem={(flight) => (
                                <Card
                                    size="small"
                                    hoverable
                                    style={{ marginBottom: 10 }}
                                    onClick={() => navigate(`/ramp-checklist/${flight.leg_id}`)}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Space wrap size={8}>
                                                <Text strong style={{ fontSize: 16 }}>
                                                    {flight.flight_no}
                                                </Text>
                                                <Tag>{flight.aircraft_reg || "无机号"}</Tag>
                                                <Tag color={STATUS_COLORS[flight.status] || "blue"}>{flight.status}</Tag>
                                            </Space>
                                            <div style={{ marginTop: 4 }}>
                                                <Text>
                                                    {flight.dep} → {flight.arr}
                                                </Text>
                                                <Text type="secondary" style={{ marginLeft: 12 }}>
                                                    STD {utcToLocalHm(flight.std, tzId)} · STA {utcToLocalHm(flight.sta, tzId)}
                                                </Text>
                                            </div>
                                            <div style={{ marginTop: 6 }}>{progressTag(flight.checklist)}</div>
                                        </div>
                                        <RightOutlined style={{ color: "#bbb" }} />
                                    </div>
                                </Card>
                            )}
                        />
                    )}
                </Spin>
            </div>
        </ConfigProvider>
    );
}
